import { FetchResult, gql } from '@apollo/client';
import { Button, Elevation, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Formik } from 'formik';
import { useSubmit } from 'formik-apollo';
import React from 'react';
import * as Yup from 'yup';
import ColorInput from '../../../components/ColorInput';
import { ContentCard, ContentCardFooter, ContentCardHeader, ContentCardScroll } from '../../../components/ContentCard';
import FormGroup from '../../../components/FormGroup';
import Text from '../../../components/Text';
import TextAreaInput from '../../../components/TextAreaInput';
import TextInput from '../../../components/TextInput';
import {
  CategoryCreateFormFragment,
  CategoryEditFormFragment,
  CreateCategoryMutation,
  UpdateCategoryInput,
  UpdateCategoryMutation,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from '../../../generated/graphql';
import { getLaravelValidationErrors, showFormErrorMessage } from '../../../helpers/graphql';
import { buildDiff } from '../../../helpers/utils';

const validationSchema = Yup.object({
  name: Yup.string().required('Erforderlich'),
  color: Yup.string().required('Erforderlich'),
});

type CategoryFormProps = {
  onCancel?: () => void;
  onCreated?: (data: CreateCategoryMutation) => void;
  onUpdated?: (data: UpdateCategoryMutation) => void;
  initialValues: CategoryCreateFormFragment | CategoryEditFormFragment;
};

const isEditData = (
  initialValues: CategoryCreateFormFragment | CategoryEditFormFragment,
): initialValues is CategoryEditFormFragment => 'id' in initialValues;

const isUpdateResult = (data: CreateCategoryMutation | UpdateCategoryMutation): data is UpdateCategoryMutation =>
  'updateCategory' in data;

const CategoryForm = ({ onCancel, onCreated, onUpdated, initialValues }: CategoryFormProps) => {
  const isEdit = isEditData(initialValues);
  const [updateCategory] = useUpdateCategoryMutation();
  const [createCategory] = useCreateCategoryMutation();
  const handleSubmit = useSubmit<
    CategoryCreateFormFragment | CategoryEditFormFragment,
    FetchResult<CreateCategoryMutation> | FetchResult<UpdateCategoryMutation>
  >({
    mutate: (values) =>
      isEditData(values)
        ? updateCategory({
            variables: {
              input: buildDiff(initialValues, values) as UpdateCategoryInput,
            },
          })
        : createCategory({
            variables: {
              input: {
                ...values,
              },
            },
          }),
    onCompleted: (res) => {
      if (!res.data) return;
      return isUpdateResult(res.data) ? onUpdated?.(res.data) : onCreated?.(res.data);
    },
    onError: showFormErrorMessage,
    getErrors: getLaravelValidationErrors,
  });

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ isSubmitting, submitForm, dirty }) => (
        <ContentCard elevation={Elevation.FOUR}>
          <ContentCardHeader
            leftElement={<Text large>Kategorie {isEdit ? 'bearbeiten' : 'hinzufügen'}</Text>}
            rightElement={<Button onClick={onCancel} icon={IconNames.CROSS} minimal />}
          />

          <ContentCardScroll>
            <FormGroup label="Name" labelInfo="(erforderlich)" name="name">
              <TextInput name="name" placeholder="Name" />
            </FormGroup>
            <FormGroup label="Farbe" labelInfo="(erforderlich)" name="color">
              <ColorInput name="color" />
            </FormGroup>

            <FormGroup name="description">
              <TextAreaInput name="description" placeholder="Beschreibung" fill rows={10} />
            </FormGroup>
          </ContentCardScroll>

          <ContentCardFooter
            rightElement={
              <>
                <Button text="Abbrechen" onClick={onCancel} className="ml-2" />
                <Button
                  text={isEdit ? 'Änderungen Sichern' : 'Hinzufügen'}
                  loading={isSubmitting}
                  disabled={!dirty}
                  intent={Intent.PRIMARY}
                  onClick={submitForm}
                  className="ml-2"
                />
              </>
            }
          />
        </ContentCard>
      )}
    </Formik>
  );
};

CategoryForm.fragments = {
  create: gql`
    fragment CategoryCreateForm on Category {
      name
      description
      color
    }
  `,
  edit: gql`
    fragment CategoryEditForm on Category {
      id
      name
      description
      color
    }
  `,
};

export default CategoryForm;
